import React from "react";

/*
 * Hand-drawn SVG sprites for the Halloween dress-up.
 *
 * Markup only: every bit of motion lives in halloween.css, keyed off the hw-*
 * classes, so a single prefers-reduced-motion rule can stop all of it at once.
 * Extra props (width, height, x, y, style, onClick…) go straight to the <svg>,
 * which also lets the footer's graveyard nest a sprite inside its own drawing.
 */

function classes(base, extra) {
  return extra ? base + " " + extra : base;
}

/** Deterministic PRNG (mulberry32): scattered decorations land the same way every render. */
export function seeded(seed) {
  var t = seed >>> 0;
  return function () {
    t = (t + 0x6d2b79f5) >>> 0;
    var r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/* ------------------------------------------------------------------ Bat */

var WING =
  "M45 21 C36 9 20 6 3 13 C9 16 11 21 9 27 C15 23 21 25 23 31 " +
  "C27 25 33 25 37 31 C39 26 42 25 45 27 Z";

/** Flapping bat. `flap={false}` folds the wings still; `flapSpeed` is seconds per beat. */
export function Bat({ className, color, eyes, flap, flapSpeed, ...rest }) {
  var fill = color || "#140F2B";
  return (
    <svg
      viewBox="0 0 100 50"
      className={classes("hw-bat", className)}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <g
        className={flap === false ? undefined : "hw-bat-wings"}
        style={flapSpeed ? { animationDuration: flapSpeed + "s" } : undefined}
      >
        <path d={WING} fill={fill} />
        <path d={WING} fill={fill} transform="matrix(-1 0 0 1 100 0)" />
      </g>
      <ellipse cx="50" cy="28" rx="6.5" ry="10" fill={fill} />
      <path
        d="M44 21 C44 16 45 14 46 13 L45.4 7.5 L48.8 11.4 L51.2 11.4 L54.6 7.5 L54 13 C55 14 56 16 56 21 Z"
        fill={fill}
      />
      {eyes !== false && (
        <g fill="#FFB347">
          <circle cx="47.8" cy="17.2" r="1.25" />
          <circle cx="52.2" cy="17.2" r="1.25" />
        </g>
      )}
    </svg>
  );
}

/* -------------------------------------------------------------- Pumpkin */

/** Jack-o'-lantern whose candle flickers. `face={false}` gives a plain pumpkin. */
export function Pumpkin({ className, face, faceDelay, ...rest }) {
  return (
    <svg
      viewBox="0 0 100 92"
      className={classes("hw-pumpkin", className)}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path
        d="M53 6 C60 1 69 3 73 9"
        fill="none"
        stroke="#5C8A2E"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path d="M46 21 C45 13 47 6 52 1 L57 4 C53 7 52 13 54 21 Z" fill="#3E5B24" />
      <ellipse cx="50" cy="56" rx="46" ry="35" fill="#C9480A" />
      <ellipse cx="29" cy="56" rx="24" ry="34" fill="#E8600F" />
      <ellipse cx="71" cy="56" rx="24" ry="34" fill="#E8600F" />
      <ellipse cx="50" cy="56" rx="19" ry="36" fill="#FF7F22" />
      <path
        d="M19 42 C22 33 28 27 35 25"
        fill="none"
        stroke="#FFB067"
        strokeOpacity="0.65"
        strokeWidth="3"
        strokeLinecap="round"
      />
      {face !== false && (
        <g
          className="hw-pumpkin-face"
          style={faceDelay ? { animationDelay: faceDelay + "s" } : undefined}
          fill="#FFD46B"
        >
          <path d="M28 48 L38 34 L44 48 Z" />
          <path d="M72 48 L62 34 L56 48 Z" />
          <path d="M46 57 L50 50 L54 57 Z" />
          <path d="M23 61 L30 66 L36 61 L43 67 L50 62 L57 67 L64 61 L70 66 L77 61 C71 78 29 78 23 61 Z" />
        </g>
      )}
    </svg>
  );
}

/* ---------------------------------------------------------------- Ghost */

export function Ghost({ className, ...rest }) {
  return (
    <svg
      viewBox="0 0 80 100"
      className={classes("hw-ghost", className)}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path
        d="M40 3 C19 3 8 21 8 42 L8 93 L17 85 L26 94 L35 85 L44 94 L53 85 L62 94 L72 86 L72 42 C72 21 61 3 40 3 Z"
        fill="#F7F3EA"
      />
      <path
        d="M58 8 C68 16 72 28 72 42 L72 86 L62 94 L62 44 C62 30 61 18 58 8 Z"
        fill="#DDD5C6"
        opacity="0.55"
      />
      <path
        d="M24 12 C17 18 14 27 13 36"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <g fill="#1B1330">
        <ellipse cx="29" cy="40" rx="5" ry="7.5" />
        <ellipse cx="51" cy="40" rx="5" ry="7.5" />
        <ellipse cx="40" cy="59" rx="5.5" ry="7.5" />
      </g>
      <g fill="#FFFFFF">
        <circle cx="30.6" cy="36.8" r="1.7" />
        <circle cx="52.6" cy="36.8" r="1.7" />
      </g>
      <g fill="#FF8FA6" opacity="0.5">
        <ellipse cx="21" cy="51" rx="4.5" ry="2.6" />
        <ellipse cx="59" cy="51" rx="4.5" ry="2.6" />
      </g>
    </svg>
  );
}

/* --------------------------------------------------------------- Spider */

/**
 * Hangs from the top-centre of its box, where the thread attaches. Mid-purple
 * rather than black so it reads on the night hero and on the white pages alike.
 */
export function Spider({ className, ...rest }) {
  return (
    <svg
      viewBox="0 0 60 52"
      className={classes("hw-spider-svg", className)}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <g
        fill="none"
        stroke="#3A2A5C"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M25 22 L13 9 L5 15 M25 25 L10 20 L2 28 M25 28 L11 32 L5 42 M26 31 L15 40 L12 50" />
        <path d="M35 22 L47 9 L55 15 M35 25 L50 20 L58 28 M35 28 L49 32 L55 42 M34 31 L45 40 L48 50" />
      </g>
      <ellipse cx="30" cy="31" rx="9" ry="11" fill="#241838" stroke="#7A64A8" strokeWidth="0.8" />
      <circle cx="30" cy="18" r="6" fill="#241838" stroke="#7A64A8" strokeWidth="0.8" />
      <path d="M27 28 L33 28 L30 32 Z M27 36 L33 36 L30 32 Z" fill="#FF7A1A" />
      <g fill="#FFB347">
        <circle cx="27.7" cy="17" r="1.5" />
        <circle cx="32.3" cy="17" r="1.5" />
      </g>
    </svg>
  );
}

/* --------------------------------------------------------------- Cobweb */

/** Corner web, anchored at the top-left of its box. Mirror it with CSS for other corners. */
var WEB = (function () {
  var spokes = [0, 15, 30, 45, 60, 75, 90].map(function (deg) {
    return (deg * Math.PI) / 180;
  });
  var rings = [16, 31, 47, 64, 83];
  var d = "";
  var i;
  var r;
  for (i = 0; i < spokes.length; i++) {
    d +=
      "M0 0 L" +
      (100 * Math.cos(spokes[i])).toFixed(1) + " " +
      (100 * Math.sin(spokes[i])).toFixed(1) + " ";
  }
  for (r = 0; r < rings.length; r++) {
    var radius = rings[r];
    for (i = 0; i < spokes.length - 1; i++) {
      var a1 = spokes[i];
      var a2 = spokes[i + 1];
      var mid = (a1 + a2) / 2;
      // Each thread sags toward the corner, which is what makes it read as silk.
      var sag = radius * 0.84;
      d +=
        "M" + (radius * Math.cos(a1)).toFixed(1) + " " + (radius * Math.sin(a1)).toFixed(1) +
        " Q" + (sag * Math.cos(mid)).toFixed(1) + " " + (sag * Math.sin(mid)).toFixed(1) +
        " " + (radius * Math.cos(a2)).toFixed(1) + " " + (radius * Math.sin(a2)).toFixed(1) + " ";
    }
  }
  return d;
})();

export function Cobweb({ className, color, ...rest }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={classes("hw-cobweb", className)}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path
        d={WEB}
        fill="none"
        stroke={color || "rgba(255,255,255,0.6)"}
        strokeWidth="0.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ---------------------------------------------------------------- Witch */

/** Witch on a broomstick with her cat, facing right. A silhouette: she reads against the moon. */
export function Witch({ className, ...rest }) {
  var ink = "#0B0918";
  return (
    <svg
      viewBox="0 0 140 80"
      className={classes("hw-witch-svg", className)}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <g fill={ink}>
        <path d="M20 57 L120 47 L120.4 50.4 L20.4 60.4 Z" />
        <path d="M23 52 C14 49 7 45 1 43 C3 50 2 58 0 66 C8 63 15 61 24 61 Z" />
        <path d="M57 37 C45 31 35 39 22 33 C30 45 42 47 55 49 Z" />
        <path d="M50 55 C50 40 58 28 68 28 C77 30 81 42 85 53 Z" />
        <circle cx="72" cy="24" r="6" />
        <path d="M77 22.5 L86 26 L77 27.5 Z" />
        <path d="M59 19.5 Q72 14.5 86 17 L86 18.8 Q72 16.6 59 21.6 Z" />
        <path d="M64.5 18.6 L80.5 16.9 L57 1 Z" />
        <path d="M80 50 L95 57 L99 55 L96 58.5 L79 53.5 Z" />
        <ellipse cx="34" cy="51" rx="6" ry="4.6" />
        <circle cx="38.5" cy="45.5" r="3.4" />
        <path d="M36 43.5 L36.4 39.2 L38.6 42.2 Z M39.6 42.2 L41.6 39.3 L41.8 43.6 Z" />
      </g>
      <g fill="none" stroke={ink} strokeLinecap="round">
        <path d="M74 36 L91 48" strokeWidth="3" />
        <path d="M66 21 C63 26 61 30 57 31" strokeWidth="1.6" />
        <path d="M28.5 52 C22 50 21 42 26 41" strokeWidth="1.8" />
      </g>
      <circle cx="39.6" cy="45" r="0.8" fill="#FFB347" />
    </svg>
  );
}

/* ------------------------------------------------------------ Witch hat */

/** Sits on the header logo. */
export function WitchHat({ className, ...rest }) {
  return (
    <svg
      viewBox="0 0 64 46"
      className={classes("hw-hat", className)}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path
        d="M16 38 C22 30 25 19 27 9 C28 3 34 0 41 4 C35 5 33 10 34 17 C35 25 41 32 48 38 Z"
        fill="#3B2566"
      />
      <path
        d="M27.5 10 C28.5 4 33.5 1.5 39 3.6"
        fill="none"
        stroke="#7A58B8"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <ellipse cx="32" cy="39.5" rx="30" ry="5.5" fill="#2A1B4E" />
      <path
        d="M18.5 34 C26 31.6 38 31.6 45.5 34 L46.6 37.4 C38 35.2 26 35.2 17.4 37.4 Z"
        fill="#FF7A1A"
      />
      <rect
        x="29"
        y="32.4"
        width="6"
        height="4.6"
        rx="0.8"
        fill="none"
        stroke="#FFD46B"
        strokeWidth="1.3"
      />
    </svg>
  );
}
